import React from 'react';

import pluralize from 'pluralize';

import { OpenInNew } from '@mui/icons-material';
import { Box, Button, Chip, GridLegacy as Grid, Typography, styled } from '@mui/material';

import Image from '~/components/Common/Image';

const RelativeImage = styled(Image)`
  position: relative;
`;

const buildUrl = (tenant: App.Tenant, path: string) => {
  let host = window.configs['LUX_CUSTOMER_PORTAL'];
  switch (tenant.value) {
    case 'scoopon':
      host = window.configs['SCOOPON_CUSTOMER_PORTAL'];
      break;
    case 'cudo':
      host = window.configs['CUDO_CUSTOMER_PORTAL'];
      break;
    case 'treatme':
      host = window.configs['TREATME_CUSTOMER_PORTAL'];
      break;
    case 'deals':
      host = window.configs['DEALS_CUSTOMER_PORTAL'];
      break;
  }
  return `${host}${path}?preview=true`;
};

const clamper = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: '1',
  WebkitBoxOrient: 'vertical',
};

export interface StatusBadge {
  label: string;
  severity: 'error' | 'warning';
}

interface Props {
  tenant: App.Tenant;
  imageId?: string;
  title?: string;
  subtitle?: string;
  path?: string;
  index: number;
  remainingDays?: number | 'OUT_OF_SCHEDULE';
  statusBadge?: StatusBadge;
  compact?: boolean;
}

function getRemainingDaysMessage(remainingDays: number) {
  if (remainingDays === 0) {
    return 'Final day';
  }
  if (remainingDays <= 3) {
    return `${remainingDays} ${pluralize('day', remainingDays)} remaining`;
  }
  return null;
}

function BaseOfferBox({
  tenant,
  imageId,
  title,
  subtitle,
  path,
  index,
  remainingDays,
  statusBadge,
  compact = false,
}: Props) {
  let remainingDaysMessage = null;

  if (remainingDays === 'OUT_OF_SCHEDULE') {
    remainingDaysMessage = 'Out of schedule';
  } else if (remainingDays !== undefined) {
    remainingDaysMessage = getRemainingDaysMessage(remainingDays);
  }
  return (
    <Grid
      container
      columnGap={1}
      alignItems="stretch"
      wrap="nowrap"
      sx={{ minHeight: 77, height: compact ? 'auto' : 77 }}
    >
      <Grid
        item
        sx={{
          width: compact ? 112 : { xs: '100px', md: '138px' },
          flexShrink: 0,
          position: 'relative',
        }}
      >
        <RelativeImage
          publicId={imageId}
          sx={{
            width: compact ? 112 : { xs: 100, md: 138 },
            height: '100%',
            minHeight: 77,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '22px',
            background: 'radial-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0))',
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <Typography color="grey.50">{index + 1}</Typography>
        </Box>
      </Grid>
      <Grid item xs padding={compact ? 0.75 : 1} minWidth={0}>
        <Grid container direction={compact ? 'column' : 'row'} gap={compact ? 0.5 : 0}>
          <Grid item xs container alignItems="center" gap={1} wrap="nowrap" minWidth={0}>
            {statusBadge && compact && (
              <Grid item sx={{ flexShrink: 0 }}>
                <Chip label={statusBadge.label} color={statusBadge.severity} size="small" />
              </Grid>
            )}
            <Grid item xs minWidth={0}>
              <Typography variant="h5" sx={{ ...clamper, WebkitLineClamp: compact ? '2' : '1' }}>
                {title}{' '}
              </Typography>
            </Grid>
            {statusBadge && !compact && (
              <Grid item sx={{ flexShrink: 0 }}>
                <Chip label={statusBadge.label} color={statusBadge.severity} size="small" />
              </Grid>
            )}
            <Grid item xs display={compact ? 'none' : { xs: 'none', md: 'block' }}>
              {remainingDaysMessage && (
                <Typography variant="h5" sx={{ color: 'red' }}>
                  ({remainingDaysMessage})
                </Typography>
              )}
            </Grid>
          </Grid>
          {path && (
            <Box
              sx={{
                width: compact ? '100%' : { xs: '100%', md: 'auto' },
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Button
                size="small"
                variant="text"
                sx={compact ? { minWidth: 0, paddingLeft: 0, paddingRight: 0.5 } : undefined}
                endIcon={<OpenInNew />}
                target="_blank"
                href={buildUrl(tenant, path)}
                rel="noreferrer"
              >
                Preview
              </Button>
            </Box>
          )}
        </Grid>
        {subtitle && (
          <Typography variant="subtitle1" sx={{ ...clamper, display: compact ? 'block' : { xs: 'none', md: 'block' } }}>
            {subtitle}
          </Typography>
        )}
      </Grid>
    </Grid>
  );
}

export default BaseOfferBox;
